import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Logo, Spinner } from "@/components/common";
import { Eye, EyeOff } from "lucide-react";

export default function Register() {
    const [sp] = useSearchParams();
    const [f, setF] = useState({
        first_name: "", last_name: "", email: "", phone: "", password: "",
        date_of_birth: "", gender: "",
        city: sp.get("city") || "", state: sp.get("state") || "", zip_code: sp.get("zip") || "",
    });
    const [agree, setAgree] = useState({ adult: false, legal: false, safety: false });
    const [loading, setLoading] = useState(false);
    const [showPw, setShowPw] = useState(false);
    const [code, setCode] = useState(sp.get("code") || "");
    const [codeInfo, setCodeInfo] = useState(null);
    const [codeChecking, setCodeChecking] = useState(false);
    const applyCode = async () => {
        if (!code.trim()) return;
        setCodeChecking(true);
        try {
            const { data } = await api.post("/public/validate-code", { code: code.trim() });
            setCodeInfo(data);
        } catch { setCodeInfo({ valid: false, message: "Could not check that code." }); }
        finally { setCodeChecking(false); }
    };
    const { login } = useAuth();
    const nav = useNavigate();
    const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
    const today = new Date();
    const maxDob = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate()).toISOString().slice(0, 10);

    const submit = async (e) => {
        e.preventDefault();
        if (!agree.adult || !agree.legal || !agree.safety) {
            toast.error("Please confirm you are 18+ and accept the agreements.");
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post("/auth/register", {
                ...f,
                access_code: codeInfo?.valid ? code.trim() : undefined,
                agree_terms: agree.legal, agree_privacy: agree.legal,
                agree_community: agree.legal, agree_safety: agree.safety,
                confirm_adult: agree.adult,
            });
            login(data.token, data.user);
            toast.success("Account created! Let's set up your profile.");
            nav("/onboarding", { replace: true });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex min-h-screen flex-col bg-background">
            <div className="p-5"><Logo /></div>
            <div className="flex flex-1 items-center justify-center px-5 pb-16">
                <div className="w-full max-w-lg rounded-3xl bg-card p-8 shadow-sm">
                    <h1 className="font-display text-3xl font-700 tracking-tight text-primary">Join the Club</h1>
                    <p className="mt-2 text-sm text-muted-foreground">For verified adult singles, 18 and over.</p>
                    <form onSubmit={submit} className="mt-6 space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label>First name</Label><Input data-testid="reg-first" value={f.first_name} onChange={set("first_name")} required className="mt-1.5" /></div>
                            <div><Label>Last name</Label><Input data-testid="reg-last" value={f.last_name} onChange={set("last_name")} required className="mt-1.5" /></div>
                        </div>
                        <div><Label>Email</Label><Input data-testid="reg-email" type="email" value={f.email} onChange={set("email")} required className="mt-1.5" /></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label>Mobile phone</Label><Input data-testid="reg-phone" value={f.phone} onChange={set("phone")} required placeholder="(555) 123-4567" className="mt-1.5" /></div>
                            <div>
                                <Label>Password</Label>
                                <div className="relative mt-1.5">
                                    <Input data-testid="reg-password" type={showPw ? "text" : "password"} value={f.password} onChange={set("password")} required minLength={8} className="pr-10" />
                                    <button type="button" data-testid="reg-password-toggle" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Hide password" : "Show password"} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary">
                                        {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                </div>
                                <p className="mt-1 text-xs text-muted-foreground">At least 8 characters.</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <div className="min-w-0">
                                <Label>Date of birth</Label>
                                <Input data-testid="reg-dob" type="date" max={maxDob} value={f.date_of_birth} onChange={set("date_of_birth")} required className="mt-1.5 w-full min-w-0" />
                            </div>
                            <div className="min-w-0">
                                <Label>Gender</Label>
                                <Select value={f.gender} onValueChange={(v) => setF({ ...f, gender: v })}>
                                    <SelectTrigger data-testid="reg-gender" className="mt-1.5 w-full"><SelectValue placeholder="Select" /></SelectTrigger>
                                    <SelectContent>
                                        {["Female", "Male", "Non-binary", "Prefer not to say"].map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                            <div className="min-w-0"><Label>City</Label>
                                <Input data-testid="reg-city" value={f.city} onChange={set("city")} required placeholder="e.g. Austin" className="mt-1.5 w-full" />
                            </div>
                            <div><Label>State</Label><Input data-testid="reg-state" value={f.state} onChange={set("state")} required placeholder="e.g. TX" className="mt-1.5" /></div>
                            <div><Label>ZIP</Label><Input data-testid="reg-zip" value={f.zip_code} onChange={set("zip_code")} required className="mt-1.5" /></div>
                        </div>
                        <div className="rounded-xl border border-dashed border-accent/40 bg-accent/5 p-4">
                            <Label>Have an access or partner code?</Label>
                            <div className="mt-1.5 flex gap-2">
                                <Input data-testid="reg-access-code" value={code} onChange={(e) => { setCode(e.target.value); setCodeInfo(null); }} placeholder="Enter code" className="uppercase" />
                                <Button type="button" variant="outline" data-testid="reg-apply-code" onClick={applyCode} disabled={codeChecking}>{codeChecking ? "…" : "Apply"}</Button>
                            </div>
                            {codeInfo && (
                                <p data-testid="reg-code-result" className={`mt-2 text-sm ${codeInfo.valid ? "text-emerald-600" : "text-red-500"}`}>
                                    {codeInfo.valid ? `Code applied! ${codeInfo.benefit}` : codeInfo.message}
                                </p>
                            )}
                        </div>
                        <div className="space-y-3 rounded-xl bg-secondary/60 p-4 text-sm">
                            <label className="flex items-start gap-2.5">
                                <Checkbox data-testid="reg-agree-adult" checked={agree.adult} onCheckedChange={(v) => setAgree({ ...agree, adult: !!v })} className="mt-0.5" />
                                <span className="text-muted-foreground">I confirm that I am 18 years of age or older.</span>
                            </label>
                            <label className="flex items-start gap-2.5">
                                <Checkbox data-testid="reg-agree-legal" checked={agree.legal} onCheckedChange={(v) => setAgree({ ...agree, legal: !!v })} className="mt-0.5" />
                                <span className="text-muted-foreground">
                                    I have read and agree to the <Link to="/terms" target="_blank" className="font-600 text-accent hover:underline">Membership Terms &amp; Conditions</Link>, <Link to="/privacy" target="_blank" className="font-600 text-accent hover:underline">Privacy Policy</Link>, <Link to="/community-rules" target="_blank" className="font-600 text-accent hover:underline">Community Rules &amp; Member Conduct Policy</Link>, and <Link to="/safety" target="_blank" className="font-600 text-accent hover:underline">Safety &amp; Participation Agreement</Link>.
                                </span>
                            </label>
                            <label className="flex items-start gap-2.5">
                                <Checkbox data-testid="reg-agree-safety" checked={agree.safety} onCheckedChange={(v) => setAgree({ ...agree, safety: !!v })} className="mt-0.5" />
                                <span className="text-muted-foreground">
                                    I understand that Single Social Club facilitates social activities between independent adults, and I agree to the <Link to="/safety" target="_blank" className="font-600 text-accent hover:underline">Safety &amp; Participation Agreement</Link>.
                                </span>
                            </label>
                        </div>
                        <Button type="submit" disabled={loading} data-testid="reg-submit" className="w-full rounded-full bg-accent hover:bg-accent/90 text-accent-foreground">
                            {loading ? <Spinner className="h-5 w-5 border-white/40 border-t-white" /> : "Create account"}
                        </Button>
                    </form>
                    <p className="mt-5 text-center text-sm text-muted-foreground">
                        Already a member? <Link to="/login" className="font-600 text-accent hover:underline">Sign in</Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
